import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Home, Key, TrendingUp, MapPin, ArrowRight, Building2, Ruler, FileText, Compass, IndianRupee, ChevronDown, User, Heart, ListChecks, Settings, LogOut, Menu, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import '../styles/Landing.css';
import RecommendationDeck from '../components/RecommendationDeck';
import ChatAssistant from '../components/ChatAssistant';

/* ─────────────────────────────────────────
   Floating Background Icons
   Used in: Recommendations + Services sections
───────────────────────────────────────── */
const FLOAT_ICONS = [
  // [Icon, size, top%, left%, animDuration, animDelay, animation]
  { Icon: Home,       size: 78, top: '12%',  left: '2%',   dur: '7s',   delay: '0s',    anim: 'geoFloat' },
  { Icon: MapPin,     size: 66, top: '65%',  left: '18%', dur: '9s',   delay: '0s',  anim: 'geoPulse' },
  { Icon: Key,        size: 70, top: '35%',  left: '94%',  dur: '8s',   delay: '0s',  anim: 'geoFloat' },
  { Icon: Building2,  size: 72, top: '72%',  left: '75%',  dur: '10s',  delay: '0s',    anim: 'geoPulse' },
  { Icon: TrendingUp, size: 64, top: '8%',   left: '55%',  dur: '6.5s', delay: '0s',  anim: 'geoFloat' },
  { Icon: Ruler,      size: 68, top: '50%',  left: '40%',  dur: '7.5s', delay: '0s',  anim: 'geoPulse' },
  { Icon: FileText,   size: 62, top: '80%',  left: '30%',   dur: '8.5s', delay: '0s',    anim: 'geoFloat' },
  { Icon: Compass,    size: 62, top: '25%',  left: '65%', dur: '11s',  delay: '0s',  anim: 'geoPulse' },
  { Icon: IndianRupee, size: 60, top: '90%',  left: '85%',  dur: '7s',   delay: '0s',  anim: 'geoFloat' },
  { Icon: Home,       size: 68, top: '55%',  left: '50%',  dur: '9.5s', delay: '0s',  anim: 'geoPulse' },
];

const floatAnimStyles = `
  @keyframes geoFloat {
    0%   { transform: translateY(0px);    opacity: 0.13; }
    50%  { transform: translateY(-38px);  opacity: 0.22; }
    100% { transform: translateY(0px);    opacity: 0.13; }
  }
  @keyframes geoPulse {
    0%   { transform: scale(1);    opacity: 0.10; }
    50%  { transform: scale(1.3); opacity: 0.20; }
    100% { transform: scale(1);    opacity: 0.10; }
  }
`;

const FloatingIcons = () => (
  <>
    <style>{floatAnimStyles}</style>
    {FLOAT_ICONS.map(({ Icon, size, top, left, dur, delay, anim }, i) => (
      <div
        key={i}
        style={{
          position: 'absolute',
          top,
          left,
          zIndex: 0,
          pointerEvents: 'none',
          animation: `${anim} ${dur} ease-in-out ${delay} infinite`,
          color: 'rgba(101, 78, 52, 0.55)',   /* warm brown to match cream bg */
        }}
      >
        <Icon size={size} strokeWidth={1.2} />
      </div>
    ))}
  </>
);


const Landing = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [scrolled, setScrolled]       = useState(false);
  const navigate = useNavigate();

  const { user, logout, isAuthenticated } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleLogout = () => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    logout();
    navigate('/landing');
  };

  const go = (path) => {
    setDropdownOpen(false);
    setMobileMenuOpen(false);
    navigate(path);
  };

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?';

  // Navbar becomes opaque on scroll
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setMobileMenuOpen(false);
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('keydown', handleEscape);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/home?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/home');
    }
  };

  const heroWords = ['Rentals.', 'Homes.', 'Agents.'];

  const handleTilt = (e) => {
  const card = e.currentTarget;

  const rect = card.getBoundingClientRect();

  const x = e.clientX - rect.left;
  const y = e.clientY - rect.top;

  const centerX = rect.width / 2;
  const centerY = rect.height / 2;

  const rotateX = ((y - centerY) / centerY) * -8;
  const rotateY = ((x - centerX) / centerX) * 8;

  card.style.transform = `
    perspective(1200px)
    rotateX(${rotateX}deg)
    rotateY(${rotateY}deg)
    translateY(-10px)
    scale(1.02)
  `;

  const glow = card.querySelector('.tilt-glow');

  if (glow) {
    glow.style.opacity = '1';
    glow.style.background = `
      radial-gradient(
        circle at ${x}px ${y}px,
        rgba(255,255,255,0.22),
        transparent 42%
      )
    `;
  }
};

const resetTilt = (e) => {
  const card = e.currentTarget;

  card.style.transform = `
    perspective(1200px)
    rotateX(0deg)
    rotateY(0deg)
    translateY(0px)
    scale(1)
  `;

  const glow = card.querySelector('.tilt-glow');

  if (glow) {
    glow.style.opacity = '0';
  }
};

  return (
    <div className="landing-page">

      {/* ── Navbar ── */}
      <nav className={`landing-navbar ${scrolled ? 'navbar-scrolled' : ''}`}>
        <div className="landing-navbar-content">
          <div className="landing-logo" onClick={() => navigate('/landing')}>
            <Building2 size={28} className="logo-icon" />
            <span className="logo-text">GeoState</span>
          </div>

          <div className="landing-nav-links">
            <Link to="/home?purpose=sell" className="nav-link">Buy</Link>
            <Link to="/home?purpose=rent" className="nav-link">Rent</Link>
            <span
              className="nav-link"
              onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/sell');
                } else {
                  navigate('/login', { state: { from: { pathname: '/sell' } } });
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              Sell
            </span>
            <Link to="/mortgage" className="nav-link">Mortgage</Link>
            <span
              className="nav-link"
              onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/agents');
                } else {
                  navigate('/login', { state: { from: { pathname: '/agents' } } });
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              Find an Agent
            </span>
          </div>

          <div className="landing-nav-actions">
            <a href="/help" className="nav-action">Help</a>

            {isAuthenticated ? (
              <div className="landing-dropdown-wrap" ref={dropdownRef}>
                <button
                  className={`landing-user-trigger ${dropdownOpen ? 'active' : ''}`}
                  onClick={() => setDropdownOpen(o => !o)}
                >
                  <div className="landing-avatar">{initials}</div>
                  <span className="landing-trigger-name">{user?.name}</span>
                  <ChevronDown size={14} className={`landing-chevron ${dropdownOpen ? 'rotated' : ''}`} />
                </button>

                {dropdownOpen && (
                  <div className="landing-dropdown">
                    <div className="landing-dropdown-header">
                      <div className="landing-dropdown-avatar">{initials}</div>
                      <div>
                        <p className="landing-dropdown-name">{user?.name}</p>
                        <p className="landing-dropdown-email">{user?.email}</p>
                      </div>
                    </div>
                    <div className="landing-dropdown-divider" />
                    <button className="landing-dropdown-item" onClick={() => go('/my-profile')}>
                      <User size={14} /> My Profile
                    </button>
                    <button className="landing-dropdown-item" onClick={() => go('/saved-properties')}>
                      <Heart size={14} /> Favourite Properties
                    </button>
                    <button className="landing-dropdown-item" onClick={() => go('/my-listings')}>
                      <ListChecks size={14} /> My Listings
                    </button>
                    <button className="landing-dropdown-item" onClick={() => go('/settings')}>
                      <Settings size={14} /> Settings
                    </button>
                    <div className="landing-dropdown-divider" />
                    <button className="landing-dropdown-item landing-logout" onClick={handleLogout}>
                      <LogOut size={14} /> Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="signin-button" onClick={() => navigate('/login', { state: { from: { pathname: '/landing' } } })}>
                Sign in
              </button>
            )}
            <button
              className={`mobile-menu-button ${mobileMenuOpen ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(prev => !prev)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

    <div
      className={`mobile-menu-overlay ${mobileMenuOpen ? 'active' : ''}`}
      onClick={() => setMobileMenuOpen(false)}
    />

    <div className={`mobile-menu-drawer ${mobileMenuOpen ? 'active' : ''}`}>
      <div className="mobile-menu-greeting">
        Welcome back
      </div>

      <div className="mobile-menu-header">
        <button
          className="mobile-menu-close"
          onClick={() => setMobileMenuOpen(false)}
        >
          <X size={22} />
        </button>

        {isAuthenticated ? (
          <>
            <div className="mobile-menu-avatar">
              {initials}
            </div>

            <div>
              <div className="mobile-menu-name">
                {user?.name}
              </div>

              <div className="mobile-menu-email">
                {user?.email}
              </div>
            </div>
          </>
        ) : (
          <div className="mobile-menu-title">
            Menu
          </div>
        )}

      </div>

      <div className="mobile-menu-divider" />

      <button className="mobile-menu-link" style={{ '--i': 0 }} onClick={() => go('/home?purpose=sell')}>
        Buy
      </button>

      <button className="mobile-menu-link" style={{ '--i': 1 }} onClick={() => go('/home?purpose=rent')}>
        Rent
      </button>

      <button
        className="mobile-menu-link"
        style={{ '--i': 2 }}
        onClick={() => {
          const token = localStorage.getItem('token');

          if (token) {
            go('/sell');
          } else {
            setMobileMenuOpen(false);

            navigate('/login', {
              state: {
                from: { pathname: '/sell' }
              }
            });
          }
        }}
      >
        Sell
      </button>

      <button
        className="mobile-menu-link"
        style={{ '--i': 3 }}
        onClick={() => go('/mortgage')}
      >
        Mortgage
      </button>

      <button
        className="mobile-menu-link"
        style={{ '--i': 4 }}
        onClick={() => {
          const token = localStorage.getItem('token');

          if (token) {
            go('/agents');
          } else {
            setMobileMenuOpen(false);

            navigate('/login', {
              state: {
                from: { pathname: '/agents' }
              }
            });
          }
        }}
      >
        Find an Agent
      </button>

      <button
        className="mobile-menu-link"
        style={{ '--i': 5 }}
        onClick={() => go('/help')}
      >
        Help
      </button>

      {isAuthenticated && (
        <>
          <div className="mobile-menu-divider" />

          <button
            className="mobile-menu-link"
            style={{ '--i': 6 }}
            onClick={() => go('/my-profile')}
          >
            My Profile
          </button>

          <button
            className="mobile-menu-link"
            style={{ '--i': 7 }}
            onClick={() => go('/saved-properties')}
          >
            Favourite Properties
          </button>

          <button
            className="mobile-menu-link"
            style={{ '--i': 8 }}
            onClick={() => go('/my-listings')}
          >
            My Listings
          </button>

          <button
            className="mobile-menu-link"
            style={{ '--i': 9 }}
            onClick={() => go('/settings')}
          >
            Settings
          </button>

          <button
            className="mobile-menu-link mobile-menu-logout"
            style={{ '--i': 10 }}
            onClick={handleLogout}
          >
            Logout
          </button>
        </>
      )}

    </div>

      {/* ── Hero ── */}
      <section className="hero-section">
        <div className="hero-overlay" />
        <div className="hero-content">
          <div className="hero-eyebrow">Real estate across India</div>
          <h1 className="hero-title">
            {heroWords.map((word, i) => (
              <span key={word} className="hero-word" style={{ '--i': i }}>
                {word}
              </span>
            ))}
          </h1>

          <form onSubmit={handleSearch} className="hero-search-form">
            <div className="hero-search-container">
              <MapPin size={18} className="hero-search-icon" />
              <input
                type="text"
                placeholder="Enter a city, locality, or pincode…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="hero-search-input"
              />
              <button type="submit" className="hero-search-button">
                <Search size={18} />
                <span>Search</span>
              </button>
            </div>
            <div className="hero-quick-links">
              <span>Popular:</span>
              {['Mumbai', 'Bengaluru', 'Pune', 'Hyderabad'].map((city) => (
                <button
                  key={city}
                  type="button"
                  className="quick-link"
                  onClick={() => navigate(`/home?search=${city}`)}
                >
                  {city}
                </button>
              ))}
            </div>
          </form>
        </div>

        {/* Stat strip */}
        <div className="hero-stats">
          {[
            { n: '12,000+', label: 'Active listings' },
            { n: '12',      label: 'Indian cities' },
            { n: '₹2,400Cr+', label: 'Transactions closed' },
            { n: '4.8★',    label: 'Avg. rating' },
          ].map(({ n, label }, i) => (
            <div key={label} className="hero-stat" style={{ '--i': i }}>
              <span className="stat-number">{n}</span>
              <span className="stat-label">{label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── Recommendations ── */}
      <section className="recommendations-section" style={{ position: 'relative', overflow: 'visible' }}>
        
        {/* Floating icons — behind all content */}
        <FloatingIcons />

        {/* Actual section content — above icons */}
        <div className="recommendations-content">
          <div className="recommendations-left">
            <div className="section-eyebrow">Personalised for you</div>
            <h2 className="recommendations-title">Get home<br />recommendations</h2>
            <p className="recommendations-subtitle">
              Sign in for a more personalised experience — we'll match properties
              to your budget, location preference, and lifestyle.
            </p>
            <button className="recommendations-signin-button" onClick={() => {
                const token = localStorage.getItem('token');
                if (token) {
                  navigate('/home');
                } else {
                  navigate('/login', { state: { from: { pathname: '/agents' } } });
                }
              }}>
              Find your home<ArrowRight size={16} />
            </button>
          </div>

          <div className="recommendations-right">
            <RecommendationDeck />
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section className="services-section" style={{ position: 'relative', overflow: 'hidden' }}>

        {/* Floating icons — behind all content */}
        <FloatingIcons />

        {/* Actual section content — above icons */}
        <div className="services-header">
          <div className="section-eyebrow">What we offer</div>
          <h2 className="services-section-title">Everything in one place</h2>
        </div>

        <div className="services-grid">
          <div className="service-card tilt-card" onMouseMove={handleTilt} onMouseLeave={resetTilt} style={{ '--accent-color': '#1b5e3b' }}>
            <div className="tilt-glow" />
            <div className="service-icon-wrap">
              <Home size={32} />
            </div>
            <h3 className="service-title">Buy a home</h3>
            <p className="service-description">
              A real estate agent can provide you with a clear breakdown of costs
              so that you can avoid surprise expenses.
            </p>
            <button className="service-button" onClick={() => navigate('/home?purpose=sell')}>
              Let's get you a Home <ArrowRight size={14} />
            </button>
            <div className="service-card-fill" />
          </div>

          <div className="service-card service-card-featured tilt-card" onMouseMove={handleTilt} onMouseLeave={resetTilt} style={{ '--accent-color': '#b85c38' }}>
            <div className="tilt-glow" />
            <div className="service-icon-wrap">
              <Key size={32} />
            </div>
            <h3 className="service-title">Rent a home</h3>
            <p className="service-description">
              We're creating a seamless online experience — from browsing the largest
              rental network, to applying, to paying rent.
            </p>
            <button className="service-button" onClick={() => navigate('/home?purpose=rent')}>
              Browse rentals <ArrowRight size={14} />
            </button>
            <div className="service-card-fill" />
          </div>

          <div className="service-card tilt-card" onMouseMove={handleTilt} onMouseLeave={resetTilt} style={{ '--accent-color': '#4a3728' }}>
            <div className="tilt-glow" />
            <div className="service-icon-wrap">
              <TrendingUp size={32} />
            </div>
            <h3 className="service-title">Sell a home</h3>
            <p className="service-description">
              No matter what path you take to sell your home, we can help you
              navigate a successful sale at the best price.
            </p>
            <button className="service-button" onClick={() => navigate('/sell')}>
              See your options <ArrowRight size={14} />
            </button>
            <div className="service-card-fill" />
          </div>
        </div>
      </section>

      {/* ──Geostate Footer ── */}
      <footer className="gf-root">
        <FloatingIcons />
        <div className="gf-topline"></div>

        <div className="gf-watermark">
          GeoState
        </div>

        <div className="gf-body">

          <div
            className="gf-fade"
            style={{ '--d': '0s' }}
          >

            <div className="gf-logo">
              <Building2 size={20} />
              GeoState
            </div>

            <p className="gf-tagline">
              Discover properties tailored to your life — based on
              location, budget, and lifestyle preferences.
            </p>

            <div className="gf-socials">

              <div className="gf-socials">
                <a
                  className="gf-social"
                  aria-label="X"
                  href="https://x.com/ritulmi007"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.732-8.858L2.25 2.25h6.844l4.262 5.633zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </a>
                <a
                  className="gf-social"
                  aria-label="GitHub"
                  href="https://github.com/Ritul-Ingole"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    width="14"
                    height="14"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 .5C5.649.5.5 5.649.5 12a11.5 11.5 0 008 10.95c.585.108.8-.254.8-.566 0-.279-.01-1.02-.016-2.002-3.252.707-3.938-1.568-3.938-1.568-.532-1.351-1.3-1.711-1.3-1.711-1.062-.726.08-.711.08-.711 1.174.082 1.792 1.205 1.792 1.205 1.043 1.787 2.737 1.271 3.404.972.106-.756.408-1.271.742-1.563-2.596-.295-5.325-1.298-5.325-5.777 0-1.276.455-2.32 1.2-3.138-.12-.295-.52-1.482.113-3.088 0 0 .978-.313 3.205 1.198a11.14 11.14 0 015.834 0c2.226-1.511 3.202-1.198 3.202-1.198.635 1.606.235 2.793.116 3.088.747.818 1.198 1.862 1.198 3.138 0 4.49-2.733 5.478-5.337 5.767.42.362.793 1.096.793 2.209 0 1.594-.015 2.879-.015 3.271 0 .315.211.68.806.565A11.502 11.502 0 0023.5 12C23.5 5.649 18.351.5 12 .5z" />
                  </svg>
                </a>
                <a
                  className="gf-social"
                  aria-label="LinkedIn"
                  href="https://www.linkedin.com/in/ritul-ingole-529a58298/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
              </div>

            </div>

            <div className="gf-status">
              <span className="gf-dot"></span>
              All systems operational
            </div>

          </div>

          <nav className="gf-nav">

            <div
              className="gf-fade"
              style={{ '--d': '.12s' }}
            >

              <div className="gf-col-h">
                Real Estate
              </div>

              <ul className="gf-list">

                <li>
                  <a
                    className="gf-link"
                    href="/home?purpose=sell"
                  >
                    <span className="gf-link-text">
                      Homes for sale
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/home?purpose=sell"
                  >
                    <span className="gf-link-text">
                      Foreclosures
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/home?purpose=sell"
                  >
                    <span className="gf-link-text">
                      For sale by owner
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

              </ul>

            </div>

            <div
              className="gf-fade"
              style={{ '--d': '.22s' }}
            >

              <div className="gf-col-h">
                Rentals
              </div>

              <ul className="gf-list">

                <li>
                  <a
                    className="gf-link"
                    href="/home?purpose=rent"
                  >
                    <span className="gf-link-text">
                      Apartments for rent
                    </span>
                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/home?purpose=rent"
                  >
                    <span className="gf-link-text">
                      Houses for rent
                    </span>
                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/sell"
                  >
                    <span className="gf-link-text">
                      Post a rental
                    </span>
                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>
              </ul>
            </div>

            <div
              className="gf-fade"
              style={{ '--d': '.32s' }}
            >
              <div className="gf-col-h">
                Resources
              </div>

              <ul className="gf-list">

                <li>
                  <a
                    className="gf-link"
                    href="/landing"
                  >
                    <span className="gf-link-text">
                      Blog
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/help"
                  >
                    <span className="gf-link-text">
                      Help centre
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>

                <li>
                  <a
                    className="gf-link"
                    href="/help#contact-section"
                  >
                    <span className="gf-link-text">
                      Contact us
                    </span>

                    <span className="gf-link-arr">
                      →
                    </span>
                  </a>
                </li>
              </ul>
            </div>
          </nav>
        </div>

        <div
          className="gf-bottom gf-fade"
          style={{ '--d': '.44s' }}
        >

          <div className="gf-b-logo">
            <Building2 size={14} />
            GeoState
          </div>

          <p className="gf-copy">
            © 2026 GeoState. All rights reserved.
          </p>

          <div className="gf-legal">
            <a href="/">
              Privacy
            </a>
            <span>·</span>
            <a href="/">
              Terms
            </a>
            <span>·</span>
            <a href="/">
              Cookies
            </a>

          </div>
        </div>

      </footer>
      <ChatAssistant />
    </div>
  );
};

export default Landing;